/**
 * Desktop paging inside the full-screen product photo view (zoom-dialog).
 *
 * Mouse / trackpad on screens 750px and up only: one wheel or trackpad gesture, or one arrow / Page key,
 * moves exactly one photo. Native smooth scrolling fought the scroll snap (the photo jerked back and forth
 * and short gestures sprang back), and trackpad momentum kept firing wheel events, so a gesture is ignored
 * until its events stop.
 *
 * A click on a photo does nothing here: no zoom, and Fabric's click-to-close is blocked so the view
 * doesn't vanish under the cursor. The close button and Escape close it. Touch devices keep Fabric's
 * swipe, pinch / double-tap zoom (drag-zoom-wrapper.js) and tap-to-close.
 */

// Same breakpoint as the desktop full-screen styles in product-media-gallery-content.liquid.
const finePointer = window.matchMedia('(hover: hover) and (pointer: fine) and (min-width: 750px)');

const WHEEL_THRESHOLD = 30;
const GESTURE_END_MS = 180;
const MIN_PAGE_MS = 450;

let wheelDelta = 0;
let gestureLocked = false;
let lastPageTime = 0;
/** @type {number | undefined} */
let gestureEndTimer;

/**
 * @param {Element | null} target
 * @returns {(HTMLElement & { selectThumbnail: Function, refs: { dialog: HTMLDialogElement, media: HTMLElement[] } }) | null}
 */
function openZoomDialog(target) {
  const zoomDialog = /** @type {any} */ (target?.closest('zoom-dialog'));
  return zoomDialog?.refs?.dialog?.open && typeof zoomDialog.selectThumbnail === 'function' ? zoomDialog : null;
}

/**
 * @param {ReturnType<typeof openZoomDialog>} zoomDialog
 * @param {1 | -1} step
 */
function page(zoomDialog, step) {
  if (!zoomDialog) return;
  const { dialog, media } = zoomDialog.refs;
  const current = Math.round(dialog.scrollTop / dialog.clientHeight);
  const next = Math.min(Math.max(current + step, 0), media.length - 1);

  lastPageTime = performance.now();
  if (next !== current) zoomDialog.selectThumbnail(next, { behavior: 'smooth' });
}

// Capture phase on window: Fabric's delegated on:click="/close" captures on document, and window comes first.
window.addEventListener(
  'click',
  (event) => {
    if (!finePointer.matches || !(event.target instanceof Element)) return;
    if (event.target.closest('.dialog-zoomed-gallery > .product-media-container--image')) event.stopPropagation();
  },
  { capture: true }
);

document.addEventListener(
  'wheel',
  (event) => {
    if (!finePointer.matches || !(event.target instanceof Element)) return;
    // The thumbnail strip keeps its own scrolling.
    if (event.target.closest('.dialog-thumbnails-list')) return;

    const zoomDialog = openZoomDialog(event.target);
    if (!zoomDialog) return;

    event.preventDefault();

    clearTimeout(gestureEndTimer);
    gestureEndTimer = window.setTimeout(() => {
      gestureLocked = false;
      wheelDelta = 0;
    }, GESTURE_END_MS);

    if (gestureLocked) return;

    wheelDelta += event.deltaY;
    if (Math.abs(wheelDelta) < WHEEL_THRESHOLD || performance.now() - lastPageTime < MIN_PAGE_MS) return;

    page(zoomDialog, wheelDelta > 0 ? 1 : -1);
    gestureLocked = true;
    wheelDelta = 0;
  },
  { passive: false }
);

document.addEventListener('keydown', (event) => {
  if (!finePointer.matches || !(event.target instanceof Element)) return;

  const step = { ArrowDown: 1, PageDown: 1, ArrowUp: -1, PageUp: -1 }[event.key];
  if (!step) return;

  const zoomDialog = openZoomDialog(event.target) ?? openZoomDialog(document.querySelector('zoom-dialog dialog[open]'));
  if (!zoomDialog) return;

  event.preventDefault();
  page(zoomDialog, /** @type {1 | -1} */ (step));
});
